import { parseRepScheme, targetForSet } from '../repScheme';

// Pure logic for interpreting a logged set row.
//
// This lives outside the component so it can be tested directly. The save path
// previously made these decisions inline with a single truthiness check, which
// silently discarded valid work — see classifySetRow below.

export const SET_EMPTY = 'empty';
export const SET_READY = 'ready';
export const SET_INCOMPLETE = 'incomplete';

// The backend accepts 0 reps (a failed set) and 0 or negative weight (bodyweight
// and assisted work). See docs/specs/prescription-domain.md §5.1.
const MAX_REPS = 100;

// The load bounds are NOT hardcoded here.
//
// They used to be -500 and 1000: the backend's kilogram constants, checked
// against whatever number the user typed. For a user working in pounds that
// rejected 1500 lb (680 kg, a legal sled push) and accepted 900 lb as if it
// were 900 kg — the same units defect the backend had, mirrored on the client.
//
// GET /api/preferences serves the range already expressed in the user's unit,
// rounded inward so everything inside it is something the server accepts. This
// fallback is only what applies before that call returns.
export const DEFAULT_LOAD_RANGE = { min: -500, max: 1000 };

/**
 * Decide what a set row in the logging grid means.
 *
 * The rule that matters: **a blank weight field means bodyweight, not "skip"**.
 * Nobody types "0" to log a set of push-ups — they leave the load blank and
 * enter the reps. The previous guard was:
 *
 *     if (!performance.weight || !performance.reps) return;
 *
 * Because the inputs are strings, "0" is truthy, so an explicitly typed zero did
 * survive. But a blank weight is "", which is falsy, so a set with reps entered
 * and no load was dropped — silently, and still reported as saved. That is
 * exactly how bodyweight work gets entered, so bodyweight sets never persisted.
 *
 * Reps are treated differently: there is no sensible default for "how many did
 * you do", so a row with a load and no reps is genuinely incomplete and is
 * reported back rather than guessed at.
 *
 * @param {{weight?: string|number, reps?: string|number}} row
 * @param {{min: number, max: number}} [loadRange] bounds in the user's own
 *   unit, from GET /api/preferences
 * @returns {{status: string, weight?: number, reps?: number, reason?: string}}
 */
export function classifySetRow(row, loadRange = DEFAULT_LOAD_RANGE) {
  const weightRaw = String(row?.weight ?? '').trim();
  const repsRaw = String(row?.reps ?? '').trim();

  // An untouched row is not an error; the grid always renders more rows than
  // most sessions use.
  if (weightRaw === '' && repsRaw === '') {
    return { status: SET_EMPTY };
  }

  if (repsRaw === '') {
    return { status: SET_INCOMPLETE, reason: 'reps not entered' };
  }

  const reps = Number(repsRaw);
  if (!Number.isFinite(reps) || !Number.isInteger(reps)) {
    return { status: SET_INCOMPLETE, reason: `reps "${repsRaw}" is not a whole number` };
  }
  if (reps < 0 || reps > MAX_REPS) {
    return { status: SET_INCOMPLETE, reason: `reps must be between 0 and ${MAX_REPS}` };
  }

  // Blank load = bodyweight. This is the fix.
  const weight = weightRaw === '' ? 0 : Number(weightRaw);
  if (!Number.isFinite(weight)) {
    return { status: SET_INCOMPLETE, reason: `weight "${weightRaw}" is not a number` };
  }
  const { min, max } = loadRange ?? DEFAULT_LOAD_RANGE;
  if (weight < min || weight > max) {
    return { status: SET_INCOMPLETE, reason: `weight must be between ${min} and ${max}` };
  }

  return { status: SET_READY, weight, reps };
}

/**
 * Flatten the performances state into an ordered list of sets to POST, plus the
 * rows that were skipped and why.
 *
 * Returning the skipped rows is the other half of the fix: the save handler used
 * to drop rows and then report unconditional success, so a user could log a
 * whole session, see "saved successfully", and have nothing persist.
 *
 * @param {Array<{id: number, exerciseName?: string}>} exercises
 * @param {Object} performances - { [exerciseId]: { [setNumber]: row } }
 * @param {{min: number, max: number}} [loadRange] bounds in the user's own unit
 * @returns {{ready: Array, incomplete: Array}}
 */
export function collectSetsToSave(exercises, performances, loadRange = DEFAULT_LOAD_RANGE) {
  const ready = [];
  const incomplete = [];

  for (const exercise of exercises ?? []) {
    const rows = performances?.[exercise.id];
    if (!rows) continue;

    // Numeric order, so sets post as 1, 2, 3 rather than in object-key order.
    const setNumbers = Object.keys(rows)
      .map(Number)
      .filter((n) => Number.isInteger(n) && n > 0)
      .sort((a, b) => a - b);

    for (const setNumber of setNumbers) {
      const result = classifySetRow(rows[setNumber], loadRange);

      if (result.status === SET_READY) {
        ready.push({
          exerciseId: exercise.id,
          exerciseName: exercise.exerciseName,
          setNumber,
          weight: result.weight,
          reps: result.reps,
        });
      } else if (result.status === SET_INCOMPLETE) {
        incomplete.push({
          exerciseId: exercise.id,
          exerciseName: exercise.exerciseName,
          setNumber,
          reason: result.reason,
        });
      }
    }
  }

  return { ready, incomplete };
}

/**
 * The prescribed target for one set row, 1-indexed.
 *
 * A wave names each set — "11,9,7,5" is four different targets — and that was
 * invisible while reps was a single integer: the grid showed four identical boxes
 * and the real prescription lived in a note nothing could read.
 *
 * The spare row beyond the prescription has no target rather than a wrong one.
 *
 * @param {{repScheme?: string, reps?: number}} exercise
 * @param {number} setNumber
 */
export function setTarget(exercise, setNumber) {
  const raw = exercise?.repScheme || (exercise?.reps ? String(exercise.reps) : '');
  if (!raw) return '';
  return targetForSet(parseRepScheme(raw), setNumber);
}

/**
 * How many set rows to render for a prescription. Follows the prescribed sets,
 * with one spare row so an extra set can be recorded, since athletes do
 * sometimes perform more than prescribed.
 */
export function setRowCount(prescribedSets) {
  const sets = Number(prescribedSets);
  if (!Number.isFinite(sets) || sets < 1) return 3;
  return Math.min(Math.floor(sets) + 1, 20);
}

/**
 * Today's date as YYYY-MM-DD in the user's own timezone, which is the format the
 * API expects for sessionDate.
 *
 * The save path used `new Date().toISOString().split('T')[0]`, which is UTC. For
 * anyone west of Greenwich an evening session crosses midnight UTC and was filed
 * under tomorrow's date — a 7pm session in New York logged as the next day. Since
 * training history is grouped and displayed by date, that split one session
 * across two days.
 *
 * @param {Date} [now] injectable for testing
 */
export function todayLocalISODate(now = new Date()) {
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Format a calendar date from the API (YYYY-MM-DD) for display.
 *
 * Deliberately does NOT do `new Date(isoDate).toLocaleDateString()`. Per the ECMA
 * spec a date-only string is parsed as **UTC midnight**, so rendering it in a
 * timezone behind UTC moves it to the previous day: `new Date('2026-07-24')`
 * shown in New York reads 23 July. Combined with the server previously
 * serialising the date as a timestamp, a set logged on the 24th displayed as the
 * 22nd — shifted once on the way out and once on the way in.
 *
 * Splitting the parts and constructing a local Date has no timezone component to
 * get wrong.
 *
 * @param {string} isoDate YYYY-MM-DD
 * @param {string} [locale] override for testing
 */
export function formatSessionDate(isoDate, locale = undefined) {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(isoDate ?? ''));
  if (!match) return String(isoDate ?? '');

  const [, year, month, day] = match;
  const local = new Date(Number(year), Number(month) - 1, Number(day));
  return local.toLocaleDateString(locale);
}

/**
 * The calendar date of a logged set, as the grouping key for history.
 *
 * The API now returns a plain YYYY-MM-DD. The `split('T')` is retained so a
 * server still emitting a timestamp degrades to its date part rather than
 * producing a key nothing matches.
 */
export function sessionDateKey(value) {
  return String(value ?? '').split('T')[0];
}
